import React, { useState } from 'react';
import { Modal, Button, Form, Alert, InputGroup } from 'react-bootstrap';
import { FaKey, FaSave, FaTimes } from 'react-icons/fa';
import axios from 'axios';

const LicenseEntryModal = ({ show, onHide, onLicenseUpdated, currentStatus }) => {
  const [licenseKey, setLicenseKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!licenseKey.trim()) {
      setError('Please enter a license key');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/license/install', {
        licenseKey: licenseKey.trim()
      }, {
        headers: { 'x-auth-token': token }
      });

      setSuccess({
        message: response.data.message,
        licenseType: response.data.licenseType,
        expirationDate: response.data.expirationDate
      });
      
      // Clear the form
      setLicenseKey('');
      
      // Notify parent component
      setTimeout(() => {
        onLicenseUpdated();
      }, 2000);
      
    } catch (err) {
      console.error('Error installing license:', err);
      setError(err.response?.data?.message || 'Failed to install license key');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setLicenseKey('');
    setError(null);
    setSuccess(null);
    onHide();
  };

  const formatLicenseKey = (value) => {
    // Remove all non-alphanumeric characters
    const cleanValue = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    
    // Format as XX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX
    // First 2 chars (prefix), then groups of 4
    if (cleanValue.length <= 2) {
      return cleanValue;
    }
    
    const prefix = cleanValue.substring(0, 2);
    const rest = cleanValue.substring(2);
    const groups = rest.match(/.{1,4}/g)?.join('-') || rest;
    const formatted = prefix + (rest.length > 0 ? '-' + groups : '');
    
    // Limit to expected length (30 characters + 7 dashes = 37 total)
    return formatted.substring(0, 37);
  };

  const handleLicenseKeyChange = (e) => {
    const formatted = formatLicenseKey(e.target.value);
    setLicenseKey(formatted);
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title>
          <FaKey className="me-2" />
          {currentStatus?.isValid ? 'Update License Key' : 'Enter License Key'}
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {currentStatus && (
            <div className="mb-3">
              <h6>Current Status:</h6>
              <div className="bg-light p-2 rounded">
                <small>
                  <strong>Status:</strong> {currentStatus.status}<br />
                  {currentStatus.licenseType && (
                    <><strong>Type:</strong> {currentStatus.licenseType}<br /></>
                  )}
                  {currentStatus.expirationDate && (
                    <><strong>Expires:</strong> {new Date(currentStatus.expirationDate).toLocaleDateString()}<br /></>
                  )}
                  {currentStatus.daysRemaining !== undefined && (
                    <><strong>Days Remaining:</strong> {currentStatus.daysRemaining}<br /></>
                  )}
                </small>
              </div>
            </div>
          )}
          
          <Form.Group className="mb-3">
            <Form.Label>License Key</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <FaKey />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="TR-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
                value={licenseKey}
                onChange={handleLicenseKeyChange}
                disabled={loading}
                style={{ fontFamily: 'monospace' }}
              />
            </InputGroup>
            <Form.Text className="text-muted">
              Enter your trial key (starts with TR-) or standard license key (starts with ST-)
            </Form.Text>
          </Form.Group>
          
          {error && (
            <Alert variant="danger">
              <strong>Error:</strong> {error}
            </Alert>
          )}
          
          {success && (
            <Alert variant="success">
              <strong>Success!</strong> {success.message}
              <br />
              <small>
                License Type: {success.licenseType}<br />
                Expires: {new Date(success.expirationDate).toLocaleDateString()}
              </small>
            </Alert>
          )}
          
          <div className="bg-info-subtle p-3 rounded">
            <h6 className="text-info">License Key Format:</h6>
            <ul className="mb-0 small">
              <li><strong>Trial Keys:</strong> TR-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX (60 days)</li>
              <li><strong>Standard Keys:</strong> ST-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX (6 months)</li>
            </ul>
          </div>
        </Modal.Body>
        
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={handleClose}
            disabled={loading}
          >
            <FaTimes className="me-1" />
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={loading || !licenseKey.trim()}
          >
            {loading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                Installing...
              </>
            ) : (
              <>
                <FaSave className="me-1" />
                Install License
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default LicenseEntryModal;
